if (!customElements.get('mervaux-travel-guide')) {
  class MervauxTravelGuide extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      this._lifecycle = (this._lifecycle || 0) + 1;
      this._timers = new Set();
      this._objectUrls = new Set();
      this._selectionToken = 0;
      this._index = 0;
      this._displayedIndex = this._displayedIndex || 0;
      this._disposed = false;
      this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._photos = [];
      try {
        this._photos = JSON.parse(this.querySelector('[data-motion-photos]')?.textContent || '[]');
      } catch {
        this._photos = [];
      }
      this._photos = (Array.isArray(this._photos) ? this._photos : []).filter((photo) => photo && typeof photo.src === 'string');
      this._displayedIndex = Math.max(0, Math.min(this._displayedIndex, this._photos.length - 1));
      this._index = this._displayedIndex;
      this._image = this.querySelector('[data-postcard-image]');
      this._status = this.querySelector('[data-postcard-status]');
      this._photoStatus = this.querySelector('[data-photo-load-status]');
      this._save = this.querySelector('[data-save-postcard]');
      this._journeyButtons = Array.from(this.querySelectorAll('[data-journey-stop]'));
      this._journeyPanels = Array.from(this.querySelectorAll('[data-journey-panel]'));
      this._visitedStops = new Set();
      this._postcard = this.querySelector('[data-postcard]');
      this._cardFront = this.querySelector('[data-card-front]');
      this._cardBack = this.querySelector('[data-card-back]');
      this._flipButton = this.querySelector('[data-flip-postcard]');
      this._shareDialog = this.querySelector('[data-share-dialog]');
      this._shareUrl = this.querySelector('[data-share-url]');
      this._shareStatus = this.querySelector('[data-share-status]');
      this._sharePreview = this.querySelector('[data-share-preview]');
      this._sharePreviewStatus = this.querySelector('[data-share-preview-status]');
      this._nativeShare = this.querySelector('[data-native-share]');
      this._sharedPhoto = null;
      this._shareToken = 0;
      this._shareAsset = null;
      this._shareBuild = null;
      this._shareUrlOnly = false;
      this._gesture = null;
      this._suppressFlipUntil = 0;
      this._postcardMedia = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
      this._postcardOffset = { x: 0, y: 0 };
      this._postcardHoverRect = null;
      const eventOptions = { signal: this._abort.signal };
      const resetPostcardMotion = () => {
        this._postcardOffset = { x: 0, y: 0 };
        this._cancelPostcardGesture();
      };
      this._postcardMedia.addEventListener('change', resetPostcardMotion, eventOptions);
      this._reduced.addEventListener('change', resetPostcardMotion, eventOptions);
      window.addEventListener('resize', resetPostcardMotion, { ...eventOptions, passive: true });
      window.addEventListener('scroll', () => { this._postcardHoverRect = null; }, { ...eventOptions, passive: true });
      this.addEventListener('click', (event) => this._onClick(event), eventOptions);
      this.addEventListener('keydown', (event) => this._onKeydown(event), eventOptions);
      this._postcard?.addEventListener('pointerdown', (event) => this._onPointerDown(event), { ...eventOptions, passive: true });
      this._postcard?.addEventListener('pointermove', (event) => this._onPointerMove(event), { ...eventOptions, passive: true });
      this._postcard?.addEventListener('pointerup', (event) => this._onPointerUp(event), { ...eventOptions, passive: true });
      this._postcard?.addEventListener('pointercancel', (event) => this._cancelPostcardGesture(event), eventOptions);
      this._postcard?.addEventListener('lostpointercapture', (event) => this._cancelPostcardGesture(event), eventOptions);
      this._postcard?.addEventListener('pointerleave', () => {
        if (!this._gesture) this._resetPostcardTilt();
      }, eventOptions);
      this._postcard?.addEventListener('dragstart', (event) => {
        if (!event.target.closest('a[href]')) event.preventDefault();
      }, eventOptions);
      window.addEventListener('pointerup', (event) => this._onPointerUp(event), { ...eventOptions, passive: true });
      this._shareDialog?.addEventListener('close', () => this._afterShareClose(), eventOptions);
      if (this._nativeShare) {
        this._nativeShare.hidden = typeof navigator.share !== 'function';
        this._nativeShare.disabled = false;
      }
      this._setUpDisclosures();
      this._setUpFilm();
      this._islandDialog = this.querySelector('[data-island-dialog]');
      this._islandDialog?.addEventListener('close', () => this._afterIslandClose(), eventOptions);
      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId;
        const index = this._photos.findIndex((photo) => photo.id === blockId);
        if (index >= 0) this._selectPhoto(index);
      }, eventOptions);
      const initialStop = this._journeyButtons.find((button) => button.getAttribute('aria-pressed') === 'true' && !this._isDisabled(button)) || this._journeyButtons.find((button) => !this._isDisabled(button));
      if (initialStop) this._selectJourney(Number(initialStop.dataset.journeyStop), false);
      this._setFlipped(false);
      if (this._photos[this._displayedIndex]) this._updatePostcardBack(this._photos[this._displayedIndex]);
      if (this._save) this._save.disabled = false;
      this.classList.add('is-ready');
      this._setUpReveal();
      if (this._photos.length < 2) {
        this.querySelectorAll('[data-photo-prev], [data-photo-next]').forEach((button) => {
          button.disabled = true;
        });
      }
      if (!this._photos.length && this._save) this._save.hidden = true;
      this._selectDeepLinkedPhoto();
    }

    disconnectedCallback() {
      this._disposed = true;
      this._lifecycle = (this._lifecycle || 0) + 1;
      this._selectionToken += 1;
      this._clearSharePreview();
      if (this._islandDialog?.open) { this._islandDialog.close(); this._afterIslandClose(); }
      if (this._shareDialog?.open) this._shareDialog.close();
      this._gesture = null;
      this._postcard?.classList.remove('is-dragging');
      this._resetPostcardTilt();
      this._sharedPhoto = null;
      if (this._legacyMediaListener) this._layoutMedia?.removeListener(this._legacyMediaListener);
      this._legacyMediaListener = null;
      this._abort?.abort();
      this._abort = null;
      this._filmObserver?.disconnect();
      this._filmObserver = null;
      this._tearDownFilm();
      this._observer?.disconnect();
      this._observer = null;
      this._timers?.forEach((timer) => clearTimeout(timer));
      this._timers?.clear();
      this._objectUrls?.forEach((url) => URL.revokeObjectURL(url));
      this._objectUrls?.clear();
      this.querySelectorAll('.is-pending').forEach((element) => element.classList.remove('is-pending'));
      this.classList.remove('is-ready');
    }

    _onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (this._postcard?.contains(target) && Date.now() < this._suppressFlipUntil && event.detail !== 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const islandPin = target.closest('[data-island-open]');
      if (islandPin) {
        if (typeof this._islandDialog?.showModal !== 'function') return;
        event.preventDefault();
        this._openIsland(Number(islandPin.dataset.islandOpen), islandPin);
        return;
      }
      if (target.closest('[data-island-close]')) { this._islandDialog?.close(); return; }
      if (target.closest('[data-island-prev]')) { this._showIsland(this._islandIndex - 1); return; }
      if (target.closest('[data-island-next]')) { this._showIsland(this._islandIndex + 1); return; }
      if (target.closest('[data-island-story]')) this._islandDialog?.close();
      if (target === this._islandDialog) {
        const rect = target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) target.close();
        return;
      }
      if (target.closest('[data-share-postcard]')) {
        this._openShareDialog(target.closest('[data-share-postcard]'));
        return;
      }
      if (target.closest('[data-share-close]')) {
        this._closeShareDialog();
        return;
      }
      if (target.closest('[data-share-copy]')) {
        this._copyShareUrl();
        return;
      }
      if (target.closest('[data-native-share]')) {
        this._nativeSharePostcard();
        return;
      }
      if (target === this._shareDialog) {
        const rect = this._shareDialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this._closeShareDialog();
        return;
      }
      if (this._navigateLocalAnchor(target, event)) return;
      const journeyStop = target.closest('[data-journey-stop]');
      if (journeyStop && this.contains(journeyStop)) {
        if (!this._isDisabled(journeyStop)) this._selectJourney(Number(journeyStop.dataset.journeyStop));
        return;
      }
      const journeyPhoto = target.closest('[data-journey-photo]');
      if (journeyPhoto && this.contains(journeyPhoto)) {
        if (this._isDisabled(journeyPhoto)) return;
        event.preventDefault();
        this._openJourneyPhoto(Number(journeyPhoto.dataset.journeyPhoto));
        return;
      }
      const flipButton = target.closest('[data-flip-postcard]');
      if (flipButton && this.contains(flipButton)) {
        this._setFlipped(!this._flipped);
        return;
      }
      const flipFront = target.closest('[data-flip-front]');
      if (flipFront && this.contains(flipFront)) {
        if (Date.now() < this._suppressFlipUntil && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this._setFlipped(true, true);
        return;
      }
      const flipBack = target.closest('[data-flip-back]');
      if (flipBack && this.contains(flipBack) && !target.closest('a[href]')) {
        if (Date.now() < this._suppressFlipUntil && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        this._setFlipped(false, true);
        return;
      }
      if (target.closest('[data-photo-prev]')) {
        this._selectPhoto(this._index - 1);
        return;
      }
      if (target.closest('[data-photo-next]')) {
        this._selectPhoto(this._index + 1);
        return;
      }
      if (target.closest('[data-save-postcard]')) {
        this._savePostcard();
        return;
      }
      const filmButton = target.closest('[data-video-play]');
      if (filmButton) this._loadVideo(filmButton);
    }

    _navigateLocalAnchor(target, event) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button !== undefined && event.button !== 0)) return false;
      const anchor = target.closest('a[href]');
      if (!anchor || !this.contains(anchor) || anchor.hasAttribute('download') || anchor.hasAttribute('data-journey-photo')) return false;
      const targetWindow = anchor.getAttribute('target');
      if (targetWindow && targetWindow !== '_self') return false;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('#') || href.length < 2) return false;
      let id;
      try {
        id = decodeURIComponent(href.slice(1));
      } catch {
        return false;
      }
      const destination = document.getElementById(id);
      if (!destination || !this.contains(destination)) return false;
      event.preventDefault();
      event.stopPropagation();
      try {
        const url = new URL(window.location.href);
        url.hash = href;
        if (url.hash !== window.location.hash) window.history.pushState(window.history.state, '', url.href);
      } catch {}
      this._openAncestorDetails(destination);
      destination.scrollIntoView({ block: 'start', behavior: this._reduced.matches ? 'instant' : 'smooth' });
      return true;
    }

    _onKeydown(event) {
      if (!(event.target instanceof Element)) return;
      if (event.key === 'Escape' && this._flipped && (this._postcard?.contains(event.target) || this._flipButton?.contains(event.target))) {
        event.preventDefault();
        this._setFlipped(false, true);
        return;
      }
      const postcardControl = event.target.closest('[data-flip-front], [data-flip-back], [data-flip-postcard], [data-photo-prev], [data-photo-next]');
      if (postcardControl && this.contains(postcardControl) && this._photos.length > 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
        let next;
        if (event.key === 'ArrowRight') next = this._index + 1;
        else if (event.key === 'ArrowLeft') next = this._index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this._photos.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this._selectPhoto(next);
          return;
        }
      }
      const journeyStop = event.target.closest('[data-journey-stop]');
      if (journeyStop && this.contains(journeyStop)) {
        const available = this._journeyButtons.filter((button) => !this._isDisabled(button));
        let index = available.indexOf(journeyStop);
        if (index < 0) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index += 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index -= 1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = available.length - 1;
        else return;
        event.preventDefault();
        const selected = available[(index + available.length) % available.length];
        this._selectJourney(Number(selected.dataset.journeyStop));
        selected.focus({ preventScroll: true });
        return;
      }
    }

    _setUpDisclosures() {
      const stories = this.querySelector('[data-stories-disclosure]');
      if (stories && window.location.hash.startsWith('#MotionStop-')) stories.open = true;
    }

    _openAncestorDetails(target) {
      for (let ancestor = target.parentElement; ancestor && ancestor !== this; ancestor = ancestor.parentElement) {
        if (ancestor.tagName === 'DETAILS') ancestor.open = true;
      }
    }

    _responsivePhotoSource(photo) {
      return window.matchMedia('(max-width: 749px)').matches && photo.mobileSrc ? photo.mobileSrc : photo.src;
    }

    _selectDeepLinkedPhoto() {
      let id;
      try {
        id = new URL(window.location.href).searchParams.get('postcard');
      } catch {
        return;
      }
      if (!id) return;
      const index = this._photos.findIndex((photo) => photo.id === id);
      if (index >= 0) this._selectPhoto(index);
    }

    _onPointerDown(event) {
      if (event.isPrimary === false) {
        this._cancelPostcardGesture();
        return;
      }
      if (!(event.target instanceof Element) || !this._postcard?.contains(event.target)) return;
      if (event.target.closest('a[href], [data-photo-prev], [data-photo-next]')) return;
      if (event.pointerType === 'mouse') {
        if (event.button !== 0 || !this._postcardMedia?.matches) return;
        const rect = this._postcard.getBoundingClientRect();
        this._suppressFlipUntil = 0;
        this._gesture = {
          mode: 'drag', id: event.pointerId, x: event.clientX, y: event.clientY,
          offsetX: this._postcardOffset?.x || 0, offsetY: this._postcardOffset?.y || 0,
          limitX: Math.min(52, Math.max(20, (window.innerWidth - rect.width) / 2 - 28)),
          dragged: false,
        };
        return;
      }
      if (!['touch', 'pen'].includes(event.pointerType) || this._photos.length < 2) return;
      this._suppressFlipUntil = 0;
      this._gesture = { mode: 'swipe', id: event.pointerId, x: event.clientX, y: event.clientY, vertical: false, horizontal: false };
    }

    _onPointerMove(event) {
      const gesture = this._gesture;
      if (!gesture) {
        if (event.pointerType !== 'mouse' || !this._postcardMedia?.matches || this._reduced?.matches) return;
        this._postcardHoverRect ||= this._postcard.getBoundingClientRect();
        const rect = this._postcardHoverRect;
        const x = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1));
        this._paintPostcardPose(x * 5, y * 4, -y * 4, x * 5, 50 + x * 35, 50 + y * 35);
        return;
      }
      if (!gesture || event.pointerId !== gesture.id || gesture.vertical) return;
      if (gesture.mode === 'drag') {
        const x = event.clientX - gesture.x;
        const y = event.clientY - gesture.y;
        if (!gesture.dragged && Math.hypot(x, y) < 6) return;
        if (!gesture.dragged) {
          gesture.dragged = true;
          this._postcard.classList.add('is-dragging');
          try { this._postcard.setPointerCapture(event.pointerId); } catch {}
        }
        this._postcardOffset = {
          x: Math.max(-gesture.limitX, Math.min(gesture.limitX, gesture.offsetX + x)),
          y: Math.max(-32, Math.min(32, gesture.offsetY + y)),
        };
        this._paintPostcardPose(0, 0, 0, 0);
        return;
      }
      const x = Math.abs(event.clientX - gesture.x);
      const y = Math.abs(event.clientY - gesture.y);
      if (y > 12 && y > x * 1.15) {
        gesture.vertical = true;
        return;
      }
      if (!gesture.horizontal && x > 12 && x > y * 1.25) {
        gesture.horizontal = true;
        try { this._postcard?.setPointerCapture(event.pointerId); } catch {}
      }
    }

    _onPointerUp(event) {
      const gesture = this._gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      this._gesture = null;
      if (gesture.mode === 'drag') {
        if (gesture.dragged) this._suppressFlipUntil = Date.now() + 650;
        this._postcard.classList.remove('is-dragging');
        this._resetPostcardTilt();
        try { this._postcard.releasePointerCapture(event.pointerId); } catch {}
        return;
      }
      const x = event.clientX - gesture.x;
      const y = event.clientY - gesture.y;
      if (gesture.vertical || Math.abs(x) < 12 || Math.abs(x) < Math.abs(y) * 1.35) return;
      this._suppressFlipUntil = Date.now() + 650;
      if (Math.abs(x) < 40) return;
      this._selectPhoto(this._index + (x < 0 ? 1 : -1));
    }

    _paintPostcardPose(followX, followY, rotateX, rotateY, lightX = 50, lightY = 50) {
      if (!this._postcard) return;
      const offset = this._postcardOffset || { x: 0, y: 0 };
      this._postcard.style.setProperty('--postcard-x', `${offset.x + followX}px`);
      this._postcard.style.setProperty('--postcard-y', `${offset.y + followY}px`);
      this._postcard.style.setProperty('--postcard-rotate-x', `${rotateX}deg`);
      this._postcard.style.setProperty('--postcard-rotate-y', `${rotateY}deg`);
      this._postcard.style.setProperty('--postcard-light-x', `${lightX}%`);
      this._postcard.style.setProperty('--postcard-light-y', `${lightY}%`);
    }

    _resetPostcardTilt() {
      this._postcardHoverRect = null;
      this._paintPostcardPose(0, 0, 0, 0);
    }

    _cancelPostcardGesture(event) {
      if (event && this._gesture && event.pointerId !== this._gesture.id) return;
      if (this._gesture?.dragged) this._suppressFlipUntil = Date.now() + 650;
      this._gesture = null;
      this._postcard?.classList.remove('is-dragging');
      this._resetPostcardTilt();
    }

    _publicPostcardUrl(photo) {
      // Preview tooling may rewrite DOM URLs; sharing always uses the public destination.
      return `https://mervaux.com/pages/in-motion?postcard=${encodeURIComponent(String(photo.id || ''))}`;
    }

    _shareMessage(key) {
      return this._shareDialog?.dataset[key] || this.dataset[key] || '';
    }

    _openShareDialog(opener) {
      if (!this._shareDialog || !this._photos.length) return;
      this._clearSharePreview();
      this._sharedPhoto = this._photos[this._displayedIndex];
      this._shareUrlOnly = false;
      this._shareOpener = opener;
      if (this._shareUrl) this._shareUrl.value = this._publicPostcardUrl(this._sharedPhoto);
      if (this._shareStatus) this._shareStatus.textContent = '';
      if (this._status) this._status.textContent = '';
      this._showFallback(false);
      if (!this._shareDialog.open) this._shareDialog.showModal();
      this._shareDialog.querySelector('[data-share-close]')?.focus({ preventScroll: true });
      return this._prepareSharePreview(this._sharedPhoto);
    }

    _closeShareDialog() {
      if (this._shareDialog?.open) this._shareDialog.close();
    }

    _afterShareClose() {
      this._clearSharePreview();
      this._sharedPhoto = null;
      if (this._disposed) return;
      const opener = this._shareOpener;
      this._shareOpener = null;
      if (opener && this.contains(opener)) opener.focus({ preventScroll: true });
      else this._flipButton?.focus({ preventScroll: true });
    }

    async _copyShareUrl() {
      if (!this._sharedPhoto) return;
      const lifecycle = this._lifecycle;
      const token = this._shareToken;
      const url = this._publicPostcardUrl(this._sharedPhoto);
      if (this._shareUrl) this._shareUrl.value = url;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(url);
        if (!this._disposed && lifecycle === this._lifecycle && token === this._shareToken && this._shareStatus) this._shareStatus.textContent = this._shareMessage('copySuccess');
      } catch {
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._shareToken) return;
        if (this._shareUrl) {
          this._shareUrl.focus({ preventScroll: true });
          this._shareUrl.value = url;
          this._shareUrl.select();
        }
        if (this._shareStatus) this._shareStatus.textContent = this._shareMessage('copyError');
      }
    }

    async _nativeSharePostcard() {
      if (!this._nativeShare || this._nativeShare.disabled || typeof navigator.share !== 'function' || !this._sharedPhoto) return;
      const lifecycle = this._lifecycle;
      const photo = this._sharedPhoto;
      const token = this._shareToken;
      const file = !this._shareUrlOnly && this._shareAsset?.photo === photo ? this._shareAsset.file : null;
      const withFile = file && this._canShareFile(file);
      this._nativeShare.disabled = true;
      try {
        const data = { title: this._shareMessage('shareTitle') || this.dataset.brand || '', text: photo.caption || '' };
        if (withFile) data.files = [file];
        else data.url = this._publicPostcardUrl(photo);
        await navigator.share(data);
      } catch (error) {
        if (error.name !== 'AbortError' && !this._disposed && lifecycle === this._lifecycle && token === this._shareToken) {
          if (withFile) this._shareUrlOnly = true;
          if (this._shareStatus) this._shareStatus.textContent = this._shareMessage(withFile ? 'shareFileError' : 'shareError');
        }
      } finally {
        if (!this._disposed && lifecycle === this._lifecycle && token === this._shareToken) this._updateNativeShare();
      }
    }

    _canShareFile(file) {
      try { return typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] }); }
      catch { return false; }
    }

    _updateNativeShare() {
      if (!this._nativeShare) return;
      this._nativeShare.hidden = typeof navigator.share !== 'function';
      this._nativeShare.disabled = Boolean(this._shareBuild);
      const withFile = !this._shareUrlOnly && this._shareAsset?.file && this._canShareFile(this._shareAsset.file);
      const label = this._shareMessage(withFile ? 'shareFileLabel' : 'shareLinkLabel');
      if (label) this._nativeShare.textContent = label;
    }

    _clearSharePreview() {
      this._shareToken += 1;
      this._previewAbort?.abort();
      this._previewAbort = null;
      this._shareBuild = null;
      if (this._sharePreview) {
        this._sharePreview.hidden = true;
        this._sharePreview.removeAttribute('src');
      }
      if (this._shareAsset?.url) {
        URL.revokeObjectURL(this._shareAsset.url);
        this._objectUrls.delete(this._shareAsset.url);
      }
      this._shareAsset = null;
      if (this._sharePreviewStatus) this._sharePreviewStatus.textContent = '';
    }

    async _prepareSharePreview(photo) {
      const token = this._shareToken;
      const lifecycle = this._lifecycle;
      if (this._sharePreviewStatus) this._sharePreviewStatus.textContent = this._shareMessage('previewLoading');
      if (this._save) this._save.disabled = true;
      this._previewAbort = new AbortController();
      const build = { photo, promise: this._renderPostcard(photo, this._previewAbort.signal) };
      this._shareBuild = build;
      this._updateNativeShare();
      try {
        const blob = await build.promise;
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._shareToken || !this._shareDialog?.open) return;
        const url = URL.createObjectURL(blob);
        this._objectUrls.add(url);
        const file = typeof File === 'function' ? new File([blob], this._postcardFilename(photo), { type: 'image/png' }) : null;
        this._shareAsset = { photo, blob, file, url };
        if (this._sharePreview) {
          this._sharePreview.src = url;
          this._sharePreview.alt = [this.dataset.brand, photo.location, photo.wish, photo.productTitle].filter(Boolean).join(' · ');
          this._sharePreview.hidden = false;
        }
        if (this._sharePreviewStatus) this._sharePreviewStatus.textContent = '';
      } catch {
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._shareToken) return;
        if (this._sharePreviewStatus) this._sharePreviewStatus.textContent = this._shareMessage('previewError');
        this._showFallback(true, photo.exportSrc || photo.src);
      } finally {
        if (!this._disposed && lifecycle === this._lifecycle && token === this._shareToken) {
          this._shareBuild = null;
          if (this._save) this._save.disabled = false;
          this._updateNativeShare();
        }
      }
    }

    _isDisabled(element) {
      return element.disabled || element.getAttribute('aria-disabled') === 'true';
    }

    _selectJourney(index, markVisited = true) {
      if (!Number.isInteger(index)) return false;
      const selected = this._journeyButtons.find((button) => Number(button.dataset.journeyStop) === index);
      const panel = this._journeyPanels.find((item) => Number(item.dataset.journeyPanel) === index);
      if (!selected || !panel || this._isDisabled(selected)) return false;
      const activeElement = document.activeElement;
      const movingFocus = this._journeyPanels.some((item) => item !== panel && item.contains(activeElement));
      if (markVisited) this._visitedStops.add(index);
      this._journeyIndex = index;
      this._journeyButtons.forEach((button) => {
        const active = button === selected;
        button.setAttribute('aria-pressed', String(active));
        button.tabIndex = active ? 0 : -1;
        button.classList.toggle('is-visited', this._visitedStops.has(Number(button.dataset.journeyStop)));
      });
      this._journeyPanels.forEach((item) => {
        const active = item === panel;
        item.hidden = !active;
        item.setAttribute('aria-hidden', String(!active));
      });
      if (movingFocus) selected.focus({ preventScroll: true });
      return true;
    }

    async _openJourneyPhoto(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this._photos.length) return;
      const selected = await this._selectPhoto(index);
      if (!selected || this._disposed) return;
      const booth = this.querySelector('#photo-booth') || this._postcard;
      booth?.scrollIntoView({ block: 'start', behavior: this._reduced.matches ? 'instant' : 'smooth' });
      this._flipButton?.focus({ preventScroll: true });
    }

    _setFlipped(flipped, moveFocus = false) {
      if (!this._postcard || !this._cardFront || !this._cardBack) return;
      const next = Boolean(flipped);
      const hiddenFace = next ? this._cardFront : this._cardBack;
      const focusIsHidden = hiddenFace.contains(document.activeElement);
      if (moveFocus || focusIsHidden) this._flipButton?.focus({ preventScroll: true });
      this._flipped = next;
      this._postcard.classList.toggle('is-flipped', next);
      this._cardFront.setAttribute('aria-hidden', String(next));
      this._cardFront.inert = next;
      this._cardBack.setAttribute('aria-hidden', String(!next));
      this._cardBack.inert = !next;
      if (this._flipButton) {
        this._flipButton.setAttribute('aria-pressed', String(next));
        const label = next ? this._flipButton.dataset.unflipLabel || this.dataset.unflipLabel : this._flipButton.dataset.flipLabel || this.dataset.flipLabel;
        if (label) (this._flipButton.querySelector('[data-flip-text]') || this._flipButton).textContent = label;
      }
    }

    _updatePostcardBack(photo) {
      this._setText('[data-postcard-wish]', photo.wish);
      this._setText('[data-postcard-product-title]', photo.productTitle);
      const product = this.querySelector('[data-postcard-product]');
      const productImage = this.querySelector('[data-postcard-product-image]');
      let productUrl = null;
      try {
        const url = new URL(photo.productUrl || '', document.baseURI);
        if (photo.productUrl && ['https:', 'http:'].includes(url.protocol) && url.origin === window.location.origin) productUrl = url.href;
      } catch {
        productUrl = null;
      }
      if (product) {
        product.hidden = !productUrl || !photo.productTitle;
        if (productUrl) product.href = productUrl;
        else product.removeAttribute('href');
        if (photo.productTitle) product.setAttribute('aria-label', photo.productTitle);
        else product.removeAttribute('aria-label');
      }
      if (productImage) {
        productImage.hidden = !photo.productImage || !productUrl;
        productImage.removeAttribute('srcset');
        productImage.removeAttribute('sizes');
        productImage.alt = photo.productAlt || photo.productTitle || '';
        if (photo.productImage && productUrl) productImage.src = photo.productImage;
        else productImage.removeAttribute('src');
      }
    }

    _setUpReveal() {
      if (this._reduced.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) return;
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          entry.target.classList.remove('is-pending');
          this._observer?.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px 25px 0px' });
      this.querySelectorAll('[data-reveal]').forEach((element) => {
        if (element.getBoundingClientRect().top < window.innerHeight) return;
        element.classList.add('is-pending');
        this._observer.observe(element);
      });
    }

    async _selectPhoto(index) {
      if (!this._photos.length || !this._image || !Number.isInteger(index)) return false;
      const direction = index >= this._index ? 'is-moving-next' : 'is-moving-prev';
      this._postcard?.classList.remove('is-moving-next', 'is-moving-prev');
      this._postcard?.classList.add('is-changing', direction);
      if (this._photoStatus) this._photoStatus.textContent = '';
      this._index = ((index % this._photos.length) + this._photos.length) % this._photos.length;
      const selected = this._index;
      const photo = this._photos[selected];
      const token = ++this._selectionToken;
      const lifecycle = this._lifecycle;
      this._setFlipped(false);
      try {
        const source = this._responsivePhotoSource(photo);
        await this._loadImage(source);
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._selectionToken) return false;
        this._image.removeAttribute('srcset');
        this._image.removeAttribute('sizes');
        this._image.src = source;
        this._displayedIndex = selected;
        this._setFlipped(false);
        this._updatePostcardBack(photo);
        this._image.alt = photo.caption || photo.location || '';
        if (photo.width) this._image.width = photo.width;
        if (photo.height) this._image.height = photo.height;
        this._setText('[data-postcard-caption]', photo.caption);
        this._setText('[data-postcard-location]', photo.location);
        this._setText('[data-postcard-mood]', photo.mood);
        this._setText('[data-postcard-count]', `${String(selected + 1).padStart(2, '0')} / ${String(this._photos.length).padStart(2, '0')}`);
        if (this._photoStatus) this._photoStatus.textContent = '';
        return true;
      } catch {
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._selectionToken) return false;
        if (this._photoStatus) this._photoStatus.textContent = this.dataset.photoLoadError || '';
        return false;
      } finally {
        if (!this._disposed && lifecycle === this._lifecycle && token === this._selectionToken) this._postcard?.classList.remove('is-changing', 'is-moving-next', 'is-moving-prev');
      }
    }

    _setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value || '';
    }

    _showFallback(show, href) {
      const fallback = this.querySelector('[data-photo-fallback]');
      if (!fallback) return;
      if (href) fallback.href = href;
      fallback.hidden = !show;
    }

    _loadImage(src, anonymous = false, requestSignal) {
      return new Promise((resolve, reject) => {
        const signal = requestSignal || this._abort?.signal;
        if (signal?.aborted) { reject(new Error('Image request cancelled')); return; }
        const image = new Image();
        let settled = false;
        const finish = (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          this._timers.delete(timer);
          image.onload = null;
          image.onerror = null;
          signal?.removeEventListener('abort', onAbort);
          if (error) reject(error);
          else resolve(image);
        };
        const onAbort = () => { finish(new Error('Image request cancelled')); image.src = ''; };
        const timer = setTimeout(() => finish(new Error('Image request timed out')), 18000);
        this._timers.add(timer);
        if (anonymous) image.crossOrigin = 'anonymous';
        image.onload = () => finish();
        image.onerror = () => finish(new Error('Image unavailable'));
        signal?.addEventListener('abort', onAbort, { once: true });
        image.src = src;
      });
    }

    _postcardFilename(photo) {
      const slug = (photo.location || 'postcard').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 55);
      return `MERVAUX-In-Motion-${slug}.png`;
    }

    _drawContained(context, image, frame) {
      const scale = Math.min(frame.width / image.naturalWidth, frame.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      context.drawImage(image, frame.x + (frame.width - width) / 2, frame.y + (frame.height - height) / 2, width, height);
    }

    async _renderPostcard(photo, signal) {
      const [image, product] = await Promise.all([
        this._loadImage(photo.exportSrc || photo.src, true, signal),
        photo.productImage ? this._loadImage(photo.productImage, true, signal) : Promise.resolve(null)
      ]);
      if (signal?.aborted) throw new Error('Postcard request cancelled');
      const canvas = document.createElement('canvas');
      canvas.width = 1600;
      canvas.height = 1200;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.fillStyle = '#f8f7f3';
      context.fillRect(0, 0, 1600, 1200);
      this._drawContained(context, image, { x: 48, y: 48, width: 1504, height: 630 });
      context.fillStyle = '#243c42';
      context.textBaseline = 'alphabetic';
      context.font = '27px Georgia, serif';
      this._drawTracked(context, this.dataset.brand || '', 64, 733, 6);
      context.textAlign = 'right';
      context.font = '19px Arial, sans-serif';
      context.fillText(photo.location || '', 1536, 733, 830);
      context.textAlign = 'left';
      context.fillStyle = '#c9c8c0';
      context.fillRect(64, 765, 1472, 1);
      context.fillStyle = '#9a765a';
      context.font = 'italic 25px Georgia, serif';
      context.fillText(photo.mood || this.dataset.series || '', 80, 827, product ? 830 : 1420);
      context.fillStyle = '#243c42';
      context.font = '35px Georgia, serif';
      this._wrapText(context, photo.wish || photo.caption || '', 80, 893, product ? 870 : 1420, 47, 5);
      if (product) {
        context.fillStyle = '#dcddd6';
        context.fillRect(1050, 805, 1, 285);
        this._drawContained(context, product, { x: 1090, y: 790, width: 405, height: 268 });
        context.fillStyle = '#243c42';
        context.font = '25px Georgia, serif';
        this._wrapText(context, photo.productTitle || '', 1090, 1099, 405, 30, 2);
      }
      context.fillStyle = '#738083';
      context.font = '17px Arial, sans-serif';
      context.fillText(this.dataset.series || '', 80, 1160, 750);
      context.textAlign = 'right';
      context.globalAlpha = 0.7;
      context.fillText('mervaux.com', 1520, 1160);
      context.globalAlpha = 1;
      return new Promise((resolve, reject) => {
        canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Postcard unavailable')), 'image/png');
      });
    }

    async _savePostcard() {
      if (!this._save || this._save.disabled || !this._photos.length) return;
      const photo = this._sharedPhoto || this._photos[this._displayedIndex];
      const lifecycle = this._lifecycle;
      const token = this._shareToken;
      this._save.disabled = true;
      if (this._status) this._status.textContent = this.dataset.saving || '';
      this._showFallback(false);
      try {
        const blob = this._shareAsset?.photo === photo ? this._shareAsset.blob : await this._renderPostcard(photo);
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._shareToken) return;
        const url = URL.createObjectURL(blob);
        this._objectUrls.add(url);
        const link = document.createElement('a');
        link.href = url;
        link.download = this._postcardFilename(photo);
        link.addEventListener('click', (event) => event.stopPropagation(), { once: true });
        document.body.append(link);
        link.click();
        link.remove();
        if (this._status) this._status.textContent = this.dataset.saved || '';
        const timer = setTimeout(() => {
          URL.revokeObjectURL(url);
          this._objectUrls.delete(url);
          this._timers.delete(timer);
        }, 45000);
        this._timers.add(timer);
      } catch {
        if (this._disposed || lifecycle !== this._lifecycle || token !== this._shareToken) return;
        if (this._status) this._status.textContent = this.dataset.saveError || '';
        this._showFallback(true, photo.exportSrc || photo.src);
      } finally {
        if (!this._disposed && lifecycle === this._lifecycle && token === this._shareToken && this._save) this._save.disabled = false;
      }
    }

    _drawTracked(context, text, x, y, spacing) {
      for (const character of text) {
        context.fillText(character, x, y);
        x += context.measureText(character).width + spacing;
      }
    }

    _wrapText(context, text, x, y, maxWidth, lineHeight, maxLines) {
      const words = text.split(/\s+/);
      let line = '';
      let lineNumber = 0;
      for (let index = 0; index < words.length; index += 1) {
        const candidate = `${line}${line ? ' ' : ''}${words[index]}`;
        if (context.measureText(candidate).width > maxWidth && line) {
          context.fillText(line, x, y + lineNumber * lineHeight);
          lineNumber += 1;
          line = words[index];
          if (lineNumber === maxLines - 1) {
            let remainder = words.slice(index).join(' ');
            if (context.measureText(remainder).width > maxWidth) {
              while (remainder.length && context.measureText(`${remainder}…`).width > maxWidth) remainder = remainder.slice(0, -1);
              remainder += '…';
            }
            context.fillText(remainder, x, y + lineNumber * lineHeight);
            return;
          }
        } else line = candidate;
      }
      if (line && lineNumber < maxLines) context.fillText(line, x, y + lineNumber * lineHeight);
    }

    _openIsland(index, trigger) {
      if (!this._islandDialog || this._islandDialog.open) return;
      this._islandTrigger = trigger;
      this._showIsland(index);
      this._islandDialog.showModal();
      this._islandScrollBefore = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    _showIsland(index) {
      const cards = Array.from(this.querySelectorAll('[data-island-card]'));
      if (!cards.length) return;
      this._islandIndex = (index + cards.length) % cards.length;
      cards.forEach((card, i) => { card.hidden = i !== this._islandIndex; });
      const current = cards[this._islandIndex];
      this._islandDialog.setAttribute('aria-labelledby', current.querySelector('h3').id);
      this.querySelector('[data-island-count]').textContent = `${this._islandIndex + 1} / ${cards.length}`;
      this.querySelectorAll('[data-island-open]').forEach(pin => pin.classList.toggle('is-selected', Number(pin.dataset.islandOpen) === this._islandIndex));
    }
    _afterIslandClose() {
      if (this._islandScrollBefore !== undefined) document.documentElement.style.overflow = this._islandScrollBefore;
      this._islandScrollBefore = undefined;
      if (!this._disposed) this._islandTrigger?.focus({preventScroll:true});
    }

    _setUpFilm() {
      this._filmButton = this.querySelector('[data-video-play]');
      this._filmContainer = this._filmButton?.closest('[data-video-container]');
      if (!this._filmContainer) return;
      this._filmStatus = this._filmContainer.closest('section')?.querySelector('[data-video-status]');
      this._filmExternal = this._filmContainer.closest('section')?.querySelector('[data-video-external]');
      this._filmState = 'idle';
      this._filmAutoAllowed = !this._reduced.matches && !navigator.connection?.saveData;
      this._filmVisible = false;
      this._filmAutoAttempted = false;
      this._filmInteractionRetried = false;
      this._filmHasPlayed = false;
      this._filmManualPaused = false;
      this._filmUserRequested = false;
      this._filmStartPending = false;
      this._filmReady = false;
      this._filmToken = (this._filmToken || 0) + 1;
      if ('IntersectionObserver' in window) {
        this._filmObserver = new IntersectionObserver((entries) => {
          this._filmVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
          if (!this._filmVisible || this._disposed || !this._filmAutoAllowed) return;
          if (!this._filmAutoAttempted) {
            this._filmAutoAttempted = true;
            this._loadVideo(this._filmButton, true);
          } else if (this._filmStartPending && this._filmReady) {
            this._filmStartPending = false;
            this._playFilm();
          }
        }, { threshold: [0, 0.35] });
        this._filmObserver.observe(this._filmContainer);
      }
      const retryAfterInteraction = (event) => {
        if (!event.isTrusted || !this._filmAutoAllowed || !this._filmVisible || this._filmState !== 'blocked' || this._filmInteractionRetried || this._filmHasPlayed || this._filmManualPaused || !this._filmReady) return;
        if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
        if (event.target instanceof Element && (event.target.closest('[data-video-play]') || event.target.closest('input, textarea, select, [contenteditable="true"]'))) return;
        this._filmInteractionRetried = true;
        this._playFilm();
      };
      document.addEventListener('click', retryAfterInteraction, { capture: true, signal: this._abort.signal });
      document.addEventListener('keydown', retryAfterInteraction, { capture: true, signal: this._abort.signal });
    }

    _loadVideo(button, automatic = false) {
      if (!button || this._disposed) return;
      const type = button.dataset.videoType;
      const id = button.dataset.videoId;
      if (!id || !/^[\w-]+$/.test(id)) return;
      const container = button.closest('[data-video-container]');
      if (!container || !['youtube', 'vimeo'].includes(type)) return;
      if (!automatic) {
        this._filmUserRequested = true;
        this._filmManualPaused = false;
      }
      if (this._filmState === 'error' || (!automatic && this._filmState === 'slow' && !this._filmReady)) {
        // A player that never became ready cannot accept playVideo(). Recreate it
        // on an explicit retry, invalidating callbacks from the stalled attempt.
        this._filmToken += 1;
        this._clearFilmTimer();
        this._filmStartPending = false;
        this._filmPlayer?.destroy();
        this._filmPlayer = null;
        this._filmReady = false;
        container.querySelector('iframe')?.remove();
      }
      if (this._filmPlayer) {
        if (this._filmReady) this._playFilm();
        return;
      }
      if (container.querySelector('iframe')) return;
      const token = ++this._filmToken;
      const iframe = document.createElement('iframe');
      iframe.title = this.dataset.videoTitle || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.src = type === 'youtube'
        ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}&rel=0&hl=en&playsinline=1&mute=0`
        : `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&dnt=1&playsinline=1&muted=0`;
      container.append(iframe);
      container.classList.add('has-player');
      if (type === 'vimeo') {
        // Preserve Vimeo's visible native controls; this path does not claim playback.
        this._setFilmState('embedded');
        if (!automatic) iframe.focus();
        return;
      }
      this._setFilmState('loading');
      this._watchFilmStart();
      this._getYouTubeAPI().then((YT) => {
        if (this._disposed || token !== this._filmToken || !iframe.isConnected) return;
        this._filmPlayer = new YT.Player(iframe, {
          events: {
            onReady: () => {
              if (this._disposed || token !== this._filmToken) return;
              this._filmReady = true;
              if (this._filmVisible || this._filmUserRequested) this._playFilm();
              else this._filmStartPending = true;
            },
            onAutoplayBlocked: () => {
              if (!this._disposed && token === this._filmToken) this._setFilmState('blocked');
            },
            onStateChange: (event) => {
              if (this._disposed || token !== this._filmToken) return;
              if (event.data === YT.PlayerState.PLAYING) {
                this._filmHasPlayed = true;
                this._filmManualPaused = false;
                const moveFocus = this._filmButton === document.activeElement;
                this._setFilmState('playing');
                if (moveFocus) this._filmPlayer.getIframe().focus();
              } else if (event.data === YT.PlayerState.BUFFERING) {
                this._setFilmState('buffering');
                this._watchFilmStart();
              } else if (event.data === YT.PlayerState.PAUSED && this._filmHasPlayed) {
                this._filmManualPaused = true;
                this._setFilmState('paused');
              } else if (event.data === YT.PlayerState.ENDED) this._setFilmState('ended');
            },
            onError: () => {
              if (!this._disposed && token === this._filmToken) this._setFilmState('error');
            },
          },
        });
      }).catch(() => {
        if (!this._disposed && token === this._filmToken) this._setFilmState('error');
      });
    }

    _getYouTubeAPI() {
      if (window.YT?.Player) return Promise.resolve(window.YT);
      if (MervauxTravelGuide._youtubeApiPromise) return MervauxTravelGuide._youtubeApiPromise;
      MervauxTravelGuide._youtubeApiPromise = new Promise((resolve, reject) => {
        const previousReady = window.onYouTubeIframeAPIReady;
        let settled = false;
        const finish = (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          if (window.onYouTubeIframeAPIReady === onReady) window.onYouTubeIframeAPIReady = previousReady;
          if (error) {
            if (script?.hasAttribute('data-mervaux-film-api')) script.remove();
            reject(error);
          }
          else resolve(window.YT);
        };
        const onReady = () => {
          try { if (typeof previousReady === 'function') previousReady(); } finally { finish(window.YT?.Player ? null : new Error('Video API unavailable')); }
        };
        const timeout = setTimeout(() => finish(new Error('Video API timed out')), 15000);
        window.onYouTubeIframeAPIReady = onReady;
        let script = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
        if (!script) {
          script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.async = true;
          script.dataset.mervauxFilmApi = '';
          document.head.append(script);
        }
        script.addEventListener('error', () => {
          if (script.hasAttribute('data-mervaux-film-api')) script.remove();
          finish(new Error('Video API could not load'));
        }, { once: true });
      }).catch((error) => {
        MervauxTravelGuide._youtubeApiPromise = null;
        throw error;
      });
      return MervauxTravelGuide._youtubeApiPromise;
    }

    _playFilm() {
      if (!this._filmReady || !this._filmPlayer || this._disposed || this._filmManualPaused) return;
      this._setFilmState('loading');
      this._watchFilmStart();
      // Audible playback only. Browser policy is handled by onAutoplayBlocked.
      this._filmPlayer.unMute();
      this._filmPlayer.playVideo();
    }

    _watchFilmStart() {
      this._clearFilmTimer();
      this._filmTimer = setTimeout(() => {
        this._timers.delete(this._filmTimer);
        this._filmTimer = null;
        if (!this._disposed && ['loading', 'buffering'].includes(this._filmState)) this._setFilmState('slow');
      }, 12000);
      this._timers.add(this._filmTimer);
    }

    _clearFilmTimer() {
      if (!this._filmTimer) return;
      clearTimeout(this._filmTimer);
      this._timers?.delete(this._filmTimer);
      this._filmTimer = null;
    }

    _setFilmState(state) {
      this._filmState = state;
      if (!this._filmContainer) return;
      this._filmContainer.dataset.filmState = state;
      const active = ['loading', 'buffering'].includes(state);
      if (!active) this._clearFilmTimer();
      if (this._filmButton) {
        this._filmButton.hidden = ['playing', 'paused', 'ended', 'embedded'].includes(state) || (state === 'buffering' && this._filmHasPlayed);
        this._filmButton.setAttribute('aria-busy', String(active));
      }
      const messageKey = active ? 'filmLoading' : { blocked: 'filmBlocked', slow: 'filmSlow', error: 'filmError' }[state];
      if (this._filmStatus) this._filmStatus.textContent = messageKey ? this._filmContainer.dataset[messageKey] || '' : '';
      if (this._filmExternal) this._filmExternal.hidden = !['slow', 'error'].includes(state);
    }

    _tearDownFilm() {
      this._filmToken = (this._filmToken || 0) + 1;
      this._clearFilmTimer();
      this._filmPlayer?.destroy();
      this._filmPlayer = null;
      this._filmReady = false;
      this._filmContainer?.querySelector('iframe')?.remove();
      this._filmContainer?.classList.remove('has-player');
      if (this._filmContainer) delete this._filmContainer.dataset.filmState;
      if (this._filmButton) {
        this._filmButton.hidden = false;
        this._filmButton.removeAttribute('aria-busy');
      }
      if (this._filmStatus) this._filmStatus.textContent = '';
      if (this._filmExternal) this._filmExternal.hidden = true;
    }
  }
  customElements.define('mervaux-travel-guide', MervauxTravelGuide);
}



